import { AlertColor } from '@mui/material';
import { InputConfig } from 'features/filters/components/AdaptiveForm/AdaptiveForm.types';
import { EmailEvent, Event, RobocallEvent, UnitEvent } from 'types';

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export const EVENT_TYPES = {
  UNIT: 'unit',
  ALERT_ACK: 'alert_ack',
  CHANGE_CONFIGURATION: 'change_configuration',
  ROBOCALL: 'robocall',
  EMAIL: 'email',
};

export const ROBOCALL_EVENT_STATUSES = {
  ANSWERED: 'answered',
  FAILED: 'failed',
  NO_ANSWER: 'no_answer',
  INITIATED: 'initiated',
  IN_PROGRESS: 'in_progress',
};

const ROBOCALL_STATUS_TRANSLATION_KEYS: Record<string, string> = {
  [ROBOCALL_EVENT_STATUSES.ANSWERED]: 'components:timeline.events.robocall.status.answered',
  [ROBOCALL_EVENT_STATUSES.FAILED]: 'components:timeline.events.robocall.status.failed',
  [ROBOCALL_EVENT_STATUSES.NO_ANSWER]: 'components:timeline.events.robocall.status.noAnswer',
  [ROBOCALL_EVENT_STATUSES.INITIATED]: 'components:timeline.events.robocall.status.initiated',
  [ROBOCALL_EVENT_STATUSES.IN_PROGRESS]: 'components:timeline.events.robocall.status.inProgress',
};

const EVENT_SEVERITY_MAP: Partial<Record<string, (e: TimelineFeedEvent) => AlertColor>> = {
  [EVENT_TYPES.ROBOCALL]: (e) => (e as TimelineRobocallEvent).severity,
  [EVENT_TYPES.ALERT_ACK]: () => 'info',
  [EVENT_TYPES.CHANGE_CONFIGURATION]: () => 'info',
  [EVENT_TYPES.EMAIL]: () => 'info',
};

const EVENT_TITLE_MAP: Partial<Record<string, (e: TimelineFeedEvent, t: TranslateFn) => string>> = {
  [EVENT_TYPES.ROBOCALL]: (_, t) => t('components:timeline.events.automatedCall'),
  [EVENT_TYPES.ALERT_ACK]: (_, t) => t('components:timeline.events.acknowledgement'),
  [EVENT_TYPES.EMAIL]: (_, t) => t('components:timeline.events.email'),
};

const EVENT_CONTENT_MAP: Partial<Record<string, (e: TimelineFeedEvent, t: TranslateFn) => string>> = {
  [EVENT_TYPES.ROBOCALL]: (e) => (e as TimelineRobocallEvent).msg,
  [EVENT_TYPES.UNIT]: (e) => (e as UnitEvent).msg,
  [EVENT_TYPES.CHANGE_CONFIGURATION]: (_, t) => t('components:timeline.events.changeConfigurationMessage'),
  [EVENT_TYPES.ALERT_ACK]: (_, t) => t('components:timeline.events.allAlertsAcknowledged'),
  [EVENT_TYPES.EMAIL]: (e) => (e as TimelineEmailEvent).msg,
};

export type TimelineRobocallEvent = {
  type: typeof EVENT_TYPES.ROBOCALL;
  eventId: number;
  tsEvent: string;
  severity: 'info' | 'error';
  source: string;
  msg: string;
  user: string;
};

export type TimelineEmailEvent = {
  type: typeof EVENT_TYPES.EMAIL;
  eventId: number;
  tsEvent: string;
  severity: 'info';
  source: string;
  msg: string;
  user: string;
};

export type TimelineFeedEvent = Event | TimelineRobocallEvent | TimelineEmailEvent;

export const TIMELINE_FILTERS = ['severity', 'ts_event', 'msg', 'watch_event_type'];

export enum TIMELINE_FILTER_KEYS {
  SEVERITY = 'severity__eq',
  MESSAGE = 'msg__contains',
  TYPE = 'watch_event_type__eq',
  DATE_FROM = 'ts_event__ge',
  DATE_TO = 'ts_event__le',
}

export const createInputConfig = (
  selectedPropertyValue: string,
  t: TranslateFn
): {
  config: InputConfig;
  TIMELINE_FILTER_LABEL_MAP: Record<string, string>;
} => {
  const TIMELINE_FILTER_LABEL_MAP = {
    [TIMELINE_FILTER_KEYS.SEVERITY]: t('components:timeline.filter.severity'),
    [TIMELINE_FILTER_KEYS.MESSAGE]: t('components:timeline.filter.message'),
    [TIMELINE_FILTER_KEYS.TYPE]: t('components:timeline.filter.type'),
    [TIMELINE_FILTER_KEYS.DATE_FROM]: t('components:timeline.filter.dateFrom'),
    [TIMELINE_FILTER_KEYS.DATE_TO]: t('components:timeline.filter.dateTo'),
  };

  const inputOptionsMap = {
    severity: [
      { label: t('components:timeline.filter.options.warning'), value: 'warning', compareFnKey: 'eq' },
      { label: t('components:timeline.filter.options.info'), value: 'info', compareFnKey: 'eq' },
      { label: t('components:timeline.filter.options.error'), value: 'error', compareFnKey: 'eq' },
    ],
    watch_event_type: [
      {
        label: t('components:timeline.filter.options.commandResponse'),
        value: 'command response',
        compareFnKey: 'eq',
      },
      {
        label: t('components:timeline.filter.options.notification'),
        value: 'notification',
        compareFnKey: 'eq',
      },
    ],
  };

  const baseConfig: InputConfig<{ compareFnKey: string }> = [
    {
      name: 'property',
      label: t('components:filters.filterKey'),
      componentType: 'select',
      defaultValue: { label: '', value: '', compareFnKey: '' },
      options: [
        { label: TIMELINE_FILTER_LABEL_MAP[TIMELINE_FILTER_KEYS.SEVERITY], value: 'severity', compareFnKey: 'eq' },
        { label: TIMELINE_FILTER_LABEL_MAP[TIMELINE_FILTER_KEYS.TYPE], value: 'watch_event_type', compareFnKey: 'eq' },
        { label: TIMELINE_FILTER_LABEL_MAP[TIMELINE_FILTER_KEYS.DATE_FROM], value: 'ts_event', compareFnKey: 'ge' },
        { label: TIMELINE_FILTER_LABEL_MAP[TIMELINE_FILTER_KEYS.DATE_TO], value: 'ts_event', compareFnKey: 'le' },
      ],
    },
  ];

  if (selectedPropertyValue === 'severity' || selectedPropertyValue === 'watch_event_type') {
    baseConfig.push({
      name: 'value',
      label: t('components:filters.filterValue'),
      componentType: 'select',
      defaultValue: { label: '', value: '', compareFnKey: '' },
      options: inputOptionsMap[selectedPropertyValue],
    });
  } else if (selectedPropertyValue === 'msg') {
    baseConfig.push({
      name: 'value',
      label: t('components:filters.filterValue'),
      componentType: 'text',
    });
  } else if (selectedPropertyValue === 'ts_event') {
    baseConfig.push({
      name: 'value',
      label: t('components:filters.filterValue'),
      componentType: 'datepicker',
      maxDate: new Date(),
    });
  }

  return {
    config: baseConfig,
    TIMELINE_FILTER_LABEL_MAP,
  };
};

const formatRobocallStatus = (value: string, t: TranslateFn) => {
  const key = ROBOCALL_STATUS_TRANSLATION_KEYS[value?.toLowerCase()];
  return key ? t(key) : value;
};

const createRobocallMessage = ({
  reason,
  callContent,
  status,
  t,
}: {
  reason: string;
  callContent: string;
  status: string;
  t: TranslateFn;
}) => {
  const lines: [string, string][] = [
    ['components:timeline.events.robocall.reasonLabel', reason],
    ['components:timeline.events.robocall.callStatusLabel', formatRobocallStatus(status, t)],
    ['components:timeline.events.robocall.callContentLabel', callContent],
  ];

  return lines.map(([key, value]) => `<strong>${t(key)}:</strong> ${value ?? '-'}`).join('\n');
};

const mapRobocallEventToTimeline = ({
  robocallEvent,
  t,
  userOrganisationId,
}: {
  robocallEvent: RobocallEvent;
  t: TranslateFn;
  userOrganisationId?: number;
}): TimelineRobocallEvent => ({
  type: EVENT_TYPES.ROBOCALL,
  eventId: robocallEvent.eventId,
  tsEvent: robocallEvent.tsEvent,
  severity: robocallEvent.recipientOrganisation?.organisationId === userOrganisationId ? 'error' : 'info',
  source: '',
  msg: createRobocallMessage({
    reason: robocallEvent.alertDescription,
    callContent: robocallEvent.content,
    status: robocallEvent.status,
    t,
  }),
  user: `${t('components:timeline.events.recipient')}: ${robocallEvent.recipientOrganisation?.name ?? '-'} | ${robocallEvent.phoneNumber ?? '-'}`,
});

const createEmailRecipients = ({
  recipientOrganisation,
  recipientEmails,
  t,
}: {
  recipientOrganisation: { name: string };
  recipientEmails: { to: string[]; cc: string[]; bcc: string[] };
  t: TranslateFn;
}) => {
  const lines = [
    recipientOrganisation?.name ?? '-',
    `${t('components:timeline.events.recipient')}: ${recipientEmails.to?.join(', ') || '-'}`,
  ];

  if (recipientEmails.cc?.length) {
    lines.push(`${t('components:timeline.events.emailRecipients.cc')}: ${recipientEmails.cc.join(', ')}`);
  }

  if (recipientEmails.bcc?.length) {
    lines.push(`${t('components:timeline.events.emailRecipients.bcc')}: ${recipientEmails.bcc.join(', ')}`);
  }

  return lines.join('\n');
};

const mapEmailEventToTimeline = ({
  emailEvent,
  t,
}: {
  emailEvent: EmailEvent;
  t: TranslateFn;
}): TimelineEmailEvent => ({
  type: EVENT_TYPES.EMAIL,
  eventId: emailEvent.eventId,
  tsEvent: emailEvent.tsEvent,
  severity: 'info',
  source: '',
  msg: emailEvent.alertDescription,
  user: createEmailRecipients({
    recipientOrganisation: emailEvent.recipientOrganisation,
    recipientEmails: emailEvent.recipientEmails,
    t,
  }),
});

export const normalizeEvents = ({
  events,
  t,
  userOrganisationId,
}: {
  events: Event[];
  t: TranslateFn;
  userOrganisationId?: number;
}): TimelineFeedEvent[] =>
  events.map<TimelineFeedEvent>((event) => {
    if (event.type === EVENT_TYPES.ROBOCALL) {
      return mapRobocallEventToTimeline({ robocallEvent: event as RobocallEvent, t, userOrganisationId });
    }

    if (event.type === EVENT_TYPES.EMAIL) {
      return mapEmailEventToTimeline({ emailEvent: event as EmailEvent, t });
    }

    if (event.type === EVENT_TYPES.UNIT) {
      return event;
    }

    return {
      ...event,
      uuid: String(event.eventId),
      severity: 'info',
      source: '',
      user: `${event.type === EVENT_TYPES.CHANGE_CONFIGURATION ? t('components:timeline.events.updatedBy') : t('components:timeline.events.acknowledgedBy')}: ${event?.user?.firstName} ${event?.user?.lastName}`,
    } as unknown as TimelineFeedEvent;
  });

export const getEventSeverity = (event: TimelineFeedEvent): AlertColor =>
  EVENT_SEVERITY_MAP[event.type]?.(event) ?? (event.severity?.toLowerCase() as AlertColor) ?? 'info';

export const getEventTitle = (event: TimelineFeedEvent, t: TranslateFn): string =>
  EVENT_TITLE_MAP[event.type]?.(event, t) ?? (event as UnitEvent).severity;

export const getEventContent = (event: TimelineFeedEvent, t: TranslateFn): string =>
  EVENT_CONTENT_MAP[event.type]?.(event, t) ?? '-';

export const getEventTimestamp = (event: TimelineFeedEvent) => event.tsEvent;
